import { useMessage } from '@messageformat/react';
import { Col, FormRow, InputError } from '@op/opds.opux';
import { Stepper } from '@op/opds.opux-stepper';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

interface Props {
  isBannerLayout: boolean;
  showGraph: boolean;
  savingAmount: number;
  initialSaving: number;
  savingTime: number;
  setSavingAmount: Dispatch<SetStateAction<number>>;
  setInitialSaving: Dispatch<SetStateAction<number>>;
  setSavingTime: Dispatch<SetStateAction<number>>;
  setDisabled: Dispatch<SetStateAction<boolean>>;
}

export const UserFormStepperRow = (props: Props): JSX.Element => {
  const {
    isBannerLayout,
    showGraph,
    savingAmount,
    initialSaving,
    savingTime,
    setSavingAmount,
    setInitialSaving,
    setSavingTime,
    setDisabled,
  } = props;
  const [tempSavingTime, setTempSavingTime] = useState(15);
  const [errorAmount, setErrorAmount] = useState('');
  const [errorTime, setErrorTime] = useState('');
  const [errorinitialSaving, setErrorinitialSaving] = useState('');
  const userFormErrorLabel = useMessage('userForm_Error');
  const euroPerMonthLabel = useMessage('euroPerMonthLabel');
  const yearsLabel = useMessage('yearsLabel');

  const validateInputTime = useCallback(
    (value: number): string => {
      if (value > 50 || value < 5 || !value) {
        setDisabled(true);
        return userFormErrorLabel;
      }
      return '';
    },
    [setDisabled, userFormErrorLabel],
  );

  const validateInputAmount = useCallback(
    (value: number): string => {
      if (value > 100000 || value < 0) {
        setDisabled(true);
        return userFormErrorLabel;
      }
      return '';
    },
    [setDisabled, userFormErrorLabel],
  );

  useEffect(() => {
    if (!errorTime.length && !errorAmount.length && !errorinitialSaving.length) {
      setDisabled(false);
    }
  }, [errorTime, errorAmount, errorinitialSaving, setDisabled]);

  useEffect(() => {
    setErrorAmount(validateInputAmount(savingAmount));
  }, [savingAmount, validateInputAmount]);

  useEffect(() => {
    setErrorinitialSaving(validateInputAmount(initialSaving));
  }, [initialSaving, validateInputAmount]);

  useEffect(() => {
    setErrorTime(validateInputTime(tempSavingTime));
  }, [tempSavingTime, validateInputTime]);

  const userFormSavingAmountLabel = useMessage('userForm_savingAmount');
  const userFormInitialSavingLabel = useMessage('userForm_initialSaving');
  const userFormSavingTimeLabel = useMessage('userForm_savingTime');

  const clampValue = (value: number, min: number, max: number): number => {
    if (value < min) return min;
    if (value > max) return max;
    return value;
  };

  const handleTimeValueChange = (newValue: number): void => {
    // Convert the number to a string and remove leading zeros
    const parsedValue = parseInt(newValue.toString().replace(/^0+/, ''), 10);
    setTempSavingTime(parsedValue);
    if (parsedValue >= 5 && parsedValue <= 50) {
      setSavingTime(parsedValue);
    }
  };

  const increaseRoundedValue = (currentValue: number, stepAmount: number): number => {
    return Math.floor(currentValue / stepAmount) * stepAmount + stepAmount;
  };

  const decreaseRoundedValue = (currentValue: number, stepAmount: number): number => {
    return Math.ceil(currentValue / stepAmount) * stepAmount - stepAmount;
  };

  return (
    <>
      <Col lg={4}>
        <FormRow label={userFormSavingAmountLabel} id="savingAmount">
          <Stepper
            inputProps={{
              'aria-describedby': 'savingAmountError',
            }}
            strict
            min={0}
            max={100000}
            initialValue={50}
            unit={euroPerMonthLabel}
            value={savingAmount}
            onChange={(newValue: number) => {
              return setSavingAmount(newValue);
            }}
            onClickIncrease={() => {
              setSavingAmount(increaseRoundedValue(savingAmount, 50));
            }}
            onClickDecrease={() => {
              setSavingAmount(decreaseRoundedValue(savingAmount, 50));
            }}
          />
          <InputError id="savingAmountError" error={errorAmount} />
        </FormRow>
      </Col>
      {(!isBannerLayout || showGraph) && (
        <Col lg={4}>
          <FormRow label={userFormInitialSavingLabel} id="initialSaving">
            <Stepper
              inputProps={{
                'aria-describedby': 'initialSavingError',
              }}
              strict
              min={0}
              max={1000000}
              initialValue={0}
              unit="€"
              value={initialSaving}
              onChange={(newValue: number) => {
                return setInitialSaving(newValue);
              }}
              onClickIncrease={() => {
                setInitialSaving(increaseRoundedValue(initialSaving, 50));
              }}
              onClickDecrease={() => {
                setInitialSaving(decreaseRoundedValue(initialSaving, 50));
              }}
            />
            <InputError id="initialSavingError" error={errorinitialSaving} />
          </FormRow>
        </Col>
      )}
      {(!isBannerLayout || showGraph) && (
        <Col lg={4}>
          <FormRow label={userFormSavingTimeLabel} id="savingTime">
            <Stepper
              inputProps={{
                'aria-describedby': 'savingTimeError',
              }}
              initialValue={15}
              unit={yearsLabel}
              value={tempSavingTime}
              onChange={handleTimeValueChange}
              onClickIncrease={() => {
                const newValue = clampValue(increaseRoundedValue(savingTime, 5), 5, 50);
                setSavingTime(newValue);
                setTempSavingTime(newValue);
              }}
              onClickDecrease={() => {
                const newValue = clampValue(decreaseRoundedValue(savingTime, 5), 5, 50);
                setSavingTime(newValue);
                setTempSavingTime(newValue);
              }}
            />
            <InputError id="savingTimeError" error={errorTime} />
          </FormRow>
        </Col>
      )}
    </>
  );
};

export default UserFormStepperRow;
